import { CompanyViewAs } from 'domain/companies/roleUtils';
import React, { ReactNode } from 'react';
import { CompanyProfile, PitchDTO } from 'types/company';
import SectionHeading from 'ui/elements/SectionHeading';
import HorizontalDictionary from 'ui/elements/dictionary/HorizontalDictionary';
import Card from 'ui/views/cards/Card';
import { formatCurrency, DEFAULT_CURRENCY } from 'util/currency';
import InlineEditableField from 'ui/elements/form/InlineEditableField';
import CurrencyInput from 'ui/elements/form/numbers/CurrencyInput';
import { companiesApi } from 'apis/CompanyAPI/companies/companiesApi';
import useLazyResource from 'util/resource/useLazyResource';
import useNotify from 'hooks/useNotify';
import currencyMap from 'util/currency';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';
import TextField from 'ui/elements/form/TextField';
import { useUpdateCompany } from 'apis/CompanyAPI/companies/useUpdateCompany';
import { COMPANY_REGISTRATION_NUMBER_MAX_LENGTH } from 'util/constants';

interface Props {
  pitch: PitchDTO;
  companyProfile: CompanyProfile;
  reloadCompany: () => void;
  viewAs: CompanyViewAs;
  hideRevenues?: boolean;
}

interface CurrencyValue {
  value?: number;
  currency: string;
}

export default function CorporateInformationSection({
  pitch,
  companyProfile,
  reloadCompany,
  viewAs,
  hideRevenues,
}: Props) {
  const canViewValuation = viewAs !== 'Follower' && viewAs !== 'Visitor';
  const isAdminForCompany = viewAs === 'Admin';
  const notify = useNotify();
  const { setLastUpdated } = useCompanyLastUpdated(pitch.id);

  // Validation functions
  const validateCurrencyValue = (currencyValue: CurrencyValue, fieldName: string): string | null => {
    // Validate currency
    if (!Object.keys(currencyMap).includes(currencyValue.currency)) {
      return `Invalid currency selected for ${fieldName.toLowerCase()}`;
    }

    // Validate maximum value (less than 10^15)
    if (currencyValue.value !== null && currencyValue.value !== undefined) {
      if (currencyValue.value >= 1000000000000000) {
        return `${fieldName} must be less than 1 000 000 000 000 000`;
      }
    }

    return null;
  };

  const [updateCompany] = useLazyResource(
    (payload: { revenues?: number; revenuesCurrency?: string; valuation?: number; valuationCurrency?: string }) =>
      companiesApi.update(pitch.id, payload),
    {
      onSuccess: () => {
        reloadCompany();
        setLastUpdated(new Date());
        notify('success', 'Company information updated successfully');
      },
      onFailure: () => notify('error', 'Something unexpected happened when trying to update company information.'),
    },
  );

  // The registration number lives on the company profile resource, not the pitch, so it needs
  // the profile cache invalidated rather than reloadCompany (which only reloads the pitch)
  const [updateCompanyProfile] = useUpdateCompany(companyProfile.id, companyProfile.slug, {
    onSuccess: () => notify('success', 'Company information updated successfully'),
  });

  // The community profile dialog hides revenues and valuation, but the registration number is
  // still worth showing there, so the card can render with that entry alone.
  if (
    !isAdminForCompany &&
    (hideRevenues || (!pitch.revenues.value && !pitch.valuation?.value)) &&
    !companyProfile.companyRegistrationNumber
  ) {
    return null;
  }

  const itemsToShow = [
    hideRevenues
      ? undefined
      : {
          key: 'Revenues',
          value: isAdminForCompany ? (
            <InlineEditableField<CurrencyValue>
              label="Add revenues"
              defaultValue={{
                value: pitch.revenues.value,
                currency: pitch.revenues.currency ?? DEFAULT_CURRENCY,
              }}
              onConfirm={currencyValue => {
                // Validate input
                const validationError = validateCurrencyValue(currencyValue, 'Revenues');
                if (validationError) {
                  notify('error', validationError);
                  return Promise.reject(new Error(validationError));
                }

                // Only update if values actually changed
                const hasChanged =
                  currencyValue.value !== pitch.revenues.value ||
                  currencyValue.currency !== (pitch.revenues.currency ?? DEFAULT_CURRENCY);

                if (!hasChanged) {
                  return Promise.resolve();
                }

                return updateCompany({
                  revenues: currencyValue.value,
                  revenuesCurrency: currencyValue.currency,
                });
              }}
              inputWrapperStyle={{
                display: 'flex',
                minHeight: '45px', // Match CurrencyInput height
              }}
              renderInputComponent={(onChange, value) => (
                <CurrencyInput
                  autoFocus
                  name="revenues"
                  value={value.value}
                  currency={value.currency}
                  onChange={v => onChange({ ...value, value: v as number | undefined })}
                  onCurrencyChange={currency => onChange({ ...value, currency })}
                  placeholder="1 000 000"
                  nullable={true}
                />
              )}
              valueComponent={
                <span className="u-flex-align-center text-body">{formatCurrency(false, pitch.revenues)}</span>
              }
            />
          ) : (
            <span className="u-flex-align-center text-body">{formatCurrency(false, pitch.revenues)}</span>
          ),
        },
    !hideRevenues && canViewValuation
      ? {
          key: 'Valuation',
          value: isAdminForCompany ? (
            <InlineEditableField<CurrencyValue>
              label="Add valuation"
              defaultValue={{
                value: pitch.valuation?.value,
                currency: pitch.valuation?.currency ?? DEFAULT_CURRENCY,
              }}
              onConfirm={currencyValue => {
                // Validate input
                const validationError = validateCurrencyValue(currencyValue, 'Valuation');
                if (validationError) {
                  notify('error', validationError);
                  return Promise.reject(new Error(validationError));
                }

                // Only update if values actually changed
                const hasChanged =
                  currencyValue.value !== pitch.valuation?.value ||
                  currencyValue.currency !== (pitch.valuation?.currency ?? DEFAULT_CURRENCY);

                if (!hasChanged) {
                  return Promise.resolve();
                }

                return updateCompany({
                  valuation: currencyValue.value,
                  valuationCurrency: currencyValue.currency,
                });
              }}
              inputWrapperStyle={{
                display: 'flex',
                minHeight: '45px', // Match CurrencyInput height
              }}
              renderInputComponent={(onChange, value) => (
                <CurrencyInput
                  autoFocus
                  name="valuation"
                  value={value.value}
                  currency={value.currency}
                  onChange={v => onChange({ ...value, value: v as number | undefined })}
                  onCurrencyChange={currency => onChange({ ...value, currency })}
                  placeholder="10 000 000"
                  nullable={true}
                />
              )}
              valueComponent={
                <div>
                  <span className="u-flex-align-center text-body">{formatCurrency(false, pitch.valuation)}</span>
                  {isAdminForCompany && (
                    <div className="text-metadata u-quarter-spacing-top">
                      Valuation is only visible to company admins, board members and shareholders
                    </div>
                  )}
                </div>
              }
            />
          ) : (
            <span className="u-flex-align-center text-body">{formatCurrency(false, pitch.valuation)}</span>
          ),
        }
      : undefined,
    isAdminForCompany || companyProfile.companyRegistrationNumber
      ? {
          key: 'Company registration number',
          value: isAdminForCompany ? (
            <InlineEditableField<string>
              label="Add company registration number"
              defaultValue={companyProfile.companyRegistrationNumber ?? ''}
              onConfirm={value =>
                updateCompanyProfile({
                  company: { companyRegistrationNumber: value.trim() || null },
                })
              }
              inputWrapperStyle={{
                display: 'flex',
                minHeight: '45px', // Match CurrencyInput height
              }}
              renderInputComponent={(onChange, value) => (
                <TextField
                  autoFocus
                  name="companyRegistrationNumber"
                  maxLength={COMPANY_REGISTRATION_NUMBER_MAX_LENGTH}
                  placeholder="e.g. 912 345 678"
                  value={value}
                  onChange={e => {
                    onChange(e.target.value);
                  }}
                />
              )}
              valueComponent={
                <span className="u-flex-align-center text-body">{companyProfile.companyRegistrationNumber}</span>
              }
            />
          ) : (
            <span className="u-flex-align-center text-body">{companyProfile.companyRegistrationNumber}</span>
          ),
        }
      : undefined,
  ].filter(it => it !== undefined) as { key: string; value: ReactNode }[];

  return (
    <div>
      <SectionHeading heading={'Corporate Information'} addMobileMargin />
      <Card>
        <HorizontalDictionary columnCount={2}>{itemsToShow}</HorizontalDictionary>
      </Card>
    </div>
  );
}
